import React, { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { getDBWithInit } from "~/lib/db";
import { nanoid } from "nanoid";
import { toast } from "sonner";
import { Check, Copy, Mail, Users } from "lucide-react";
import { eq, sql } from "drizzle-orm";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { coachPoints, coaches } from "~/lib/schemas";
import { Button } from "~/components/ui/button";
import { IconD } from "~/components/ui/icon-d";
import { EmailSignature } from "~/components/ui/email-signature";
import { PLAN_LABELS } from "~/components/plan-badge";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { requireCoachAuth } from "~/lib/coach-middleware";
import {
  type OnboardingStatus,
  calculateOnboardingStatus,
} from "~/lib/onboarding";

import type {
  CoachDetailsSelect,
  CoachExpertiseSelect,
  CoachSelect,
  OfferSelect,
  ProfileSelect,
} from "~/lib/schemas";
import { OnboardingDashboard } from "~/components/ui/onboarding-dashboard";

// Full coach type for onboarding
type CoachFull = CoachSelect & {
  profile: ProfileSelect | null;
  details: CoachDetailsSelect | null;
  mainOffer: OfferSelect | null;
  expertises: CoachExpertiseSelect[];
};

interface DashboardOnboarding extends OnboardingStatus {
  referralCode: string | null;
  pointsInfo: {
    totalEarned: number;
    totalRedeemed: number;
    available: number;
  };
  coach: CoachFull;
  profile_manually_edited: boolean;
  onboarding_completed_at: Date | null;
}

const generateReferralCode = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    if (!coach) {
      throw new Error("Coach not found");
    }

    const coachId = coach.id;
    const db = await getDBWithInit();

    // Generate a short, unique referral code
    const referralCode = nanoid(8);

    // Save it to the database
    await db
      .update(coaches)
      .set({ referralCode })
      .where(eq(coaches.id, coachId));

    return referralCode;
  });

export const getOnboardingStatus = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }): Promise<DashboardOnboarding> => {
    const coachShared = context.coach;
    if (!coachShared) {
      throw new Error("Coach not found");
    }

    const db = await getDBWithInit();

    // Fetch full coach record
    const coach = await db.query.coaches.findFirst({
      where: (c, { eq }) => eq(c.id, coachShared.id),
      with: { profile: true, details: true, mainOffer: true, expertises: true },
    });
    if (!coach) {
      throw new Error("Coach not found");
    }

    // Onboarding status
    const onboarding = calculateOnboardingStatus(
      coach,
      coach.profile,
      coach.details,
      coach.mainOffer,
    );

    // Points info
    const pointsRaw = await db
      .select({
        totalEarned: sql<number>`sum(case when type = 'earned' then points else 0 end)`,
        totalRedeemed: sql<number>`sum(case when type = 'redeemed' then points else 0 end)`,
      })
      .from(coachPoints)
      .where(eq(coachPoints.coachId, coachShared.id));

    const pointsInfo = {
      totalEarned: pointsRaw[0]?.totalEarned ?? 0,
      totalRedeemed: pointsRaw[0]?.totalRedeemed ?? 0,
      available:
        (pointsRaw[0]?.totalEarned ?? 0) - (pointsRaw[0]?.totalRedeemed ?? 0),
    };

    return {
      ...onboarding,
      referralCode: coach.referralCode,
      pointsInfo,
      coach,
      profile_manually_edited: coach.profile_manually_edited,
      onboarding_completed_at: coach.onboarding_completed_at,
    };
  });

export const getReferredCoaches = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const db = await getDBWithInit();

    const referredCoaches = await db.query.coaches.findMany({
      where: (coaches, { eq }) => eq(coaches.referredByCoachId, coach.id),
      columns: {
        id: true,
        fullName: true,
        membershipPlan: true,
      },
    });

    return referredCoaches;
  });

export const finishOnboarding = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    if (!coach) {
      throw new Error("Coach not found");
    }

    await (await getDBWithInit())
      .update(coaches)
      .set({ onboarding_completed_at: new Date() })
      .where(eq(coaches.id, coach.id));

    return { success: true };
  });

export const CopyReferralLink = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={async () => {
        const link = `https://dreamcoachmatch.com/page/coach?ref=${code}`;
        await navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }}
      title="Copy registration link with referral code"
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
};

export const CopyEmailTemplate = ({
  code,
  name,
}: {
  code: string;
  name: string;
}) => {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={async () => {
        const link = `https://dreamcoachmatch.com/page/coach?ref=${code}`;
        const template = `Hi,

Hope all is well.

I wanted to share something interesting with you. I've joined Dream Coach Match, a marketplace that helps coaches connect with clients. I think you'd really like it too!
It's free to join. You can set up your profile and list your services;  it is super easy. Here's the link to register: ${link}

They've got some great features in their premium plans too—like landing pages, email marketing, tools to sell your courses, and more. If you decide to upgrade, you'll get $50 off your first payment when you register through the link above.

Would love to see you there! Let me know if you have any questions or just want to chat about it.

Cheers,
${name}`;

        await navigator.clipboard.writeText(template);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }}
      title="Copy email template with your referral link"
    >
      {copied ? <Check className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
    </Button>
  );
};

function CoachEditProfilePage() {
  const queryClient = useQueryClient();

  const { data: onboardingStatus } = useQuery({
    queryKey: ["coach", "onboarding-status"],
    queryFn: () => getOnboardingStatus(),
  });

  const { data: referredCoaches = [] } = useQuery({
    queryKey: ["coach", "referred-coaches"],
    queryFn: () => getReferredCoaches(),
  });

  const generateCodeMutation = useMutation({
    mutationFn: () => generateReferralCode(),
    onSuccess: () => {
      toast.success("Referral code generated successfully");
      queryClient.invalidateQueries({
        queryKey: ["coach", "onboarding-status"],
      });
    },
    onError: () => {
      toast.error("Failed to generate referral code");
    },
  });

  const finishOnboardingMutation = useMutation({
    mutationFn: () => finishOnboarding(),
    onSuccess: () => {
      toast.success("Onboarding completed successfully");
      queryClient.invalidateQueries({
        queryKey: ["coach", "onboarding-status"],
      });
    },
    onError: () => {
      toast.error("Failed to complete onboarding");
    },
  });

  if (!onboardingStatus) {
    return <div>Loading...</div>;
  }

  const coach = onboardingStatus.coach;
  const code = coach.referralCode;

  return (
    <div className="space-y-6">
      {!onboardingStatus.onboarding_completed_at && (
        <OnboardingDashboard status={onboardingStatus}>
          {1 === onboardingStatus.completionScore && (
            <Button
              onClick={() => finishOnboardingMutation.mutate()}
              className="mt-4"
              disabled={finishOnboardingMutation.isPending}
            >
              Finish Onboarding
            </Button>
          )}
        </OnboardingDashboard>
      )}
      <div className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Referral Program
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <p className="text-muted-foreground text-sm">
                  Earn points by referring other coaches to Dream Coach Match.
                  Points can be redeemed for free months of Pro membership.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {code ? (
                    <CopyReferralLink code={code} />
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateCodeMutation.mutate()}
                      disabled={generateCodeMutation.isPending}
                    >
                      Generate Referral Link
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Points Balance</h4>
                  <div className="flex items-center gap-1 text-sm">
                    <IconD className="h-4 w-4" />
                    <span>{onboardingStatus.pointsInfo.available}</span>
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  {referredCoaches.length} coaches referred
                </div>
                {referredCoaches.map((coach) => (
                  <div
                    key={coach.id}
                    className="bg-card/50 flex items-center justify-between rounded-lg border px-3 py-2 text-sm"
                  >
                    <div>{coach.fullName}</div>
                    <div className="text-muted-foreground">
                      {PLAN_LABELS[coach.membershipPlan]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <EmailSignature coach={coach} />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/coach/_backend/edit-profile/")({
  head: () =>
    createNoIndexMeta(
      "Coach Dashboard – Dream Coach Match",
      "Manage your coach profile, referrals, and onboarding progress",
    ),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["coach", "onboarding-status"],
      queryFn: () => getOnboardingStatus(),
    });
    await context.queryClient.ensureQueryData({
      queryKey: ["coach", "referred-coaches"],
      queryFn: () => getReferredCoaches(),
    });
    return {};
  },
  component: CoachEditProfilePage,
});
